import { FC, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
	CompoundConfig,
	CompoundStep,
	CompletionRequirement,
	Event,
	ValidationType,
} from "../../../domain/event.interface";

interface CompoundConfigSectionProps {
	compoundConfig: CompoundConfig;
	onChange: (config: CompoundConfig) => void;
	availableEvents: Event[];
	eventsLoading: boolean;
	errors?: Record<string, string>;
}

const MAX_STEPS = 10;

const COMPLETION_LABELS: Record<CompletionRequirement, string> = {
	[CompletionRequirement.ALL_REQUIRED]: "All Required Steps",
	[CompletionRequirement.ALL_STEPS]: "All Steps",
	[CompletionRequirement.REQUIRED_ONLY]: "Required Only",
};

export const CompoundConfigSection: FC<CompoundConfigSectionProps> = ({
	compoundConfig,
	onChange,
	availableEvents,
	eventsLoading,
	errors,
}) => {
	// Filter out COMPOUND events — cannot nest compounds
	const nonCompoundEvents = useMemo(
		() =>
			availableEvents.filter(
				(e) => e.validationType !== ValidationType.COMPOUND,
			),
		[availableEvents],
	);

	const selectedEventIds = useMemo(
		() => new Set(compoundConfig.steps.map((s) => s.eventId).filter(Boolean)),
		[compoundConfig.steps],
	);

	const updateSteps = (newSteps: CompoundStep[]) => {
		// Recalculate order values
		const reordered = newSteps.map((step, idx) => ({
			...step,
			order: idx + 1,
		}));
		onChange({ ...compoundConfig, steps: reordered });
	};

	const addStep = () => {
		if (compoundConfig.steps.length >= MAX_STEPS) return;
		updateSteps([
			...compoundConfig.steps,
			{ order: compoundConfig.steps.length + 1, eventId: "", required: true },
		]);
	};

	const removeStep = (index: number) => {
		if (compoundConfig.steps.length <= 2) return;
		const newSteps = [...compoundConfig.steps];
		newSteps.splice(index, 1);
		updateSteps(newSteps);
	};

	const moveStep = (index: number, direction: "up" | "down") => {
		const targetIndex = direction === "up" ? index - 1 : index + 1;
		if (targetIndex < 0 || targetIndex >= compoundConfig.steps.length) return;
		const newSteps = [...compoundConfig.steps];
		[newSteps[index], newSteps[targetIndex]] = [
			newSteps[targetIndex],
			newSteps[index],
		];
		updateSteps(newSteps);
	};

	const updateStep = (index: number, updates: Partial<CompoundStep>) => {
		const newSteps = compoundConfig.steps.map((step, i) =>
			i === index ? { ...step, ...updates } : step,
		);
		onChange({ ...compoundConfig, steps: newSteps });
	};

	const getAvailableEventsForStep = (stepIndex: number) => {
		const currentStepEventId = compoundConfig.steps[stepIndex]?.eventId;
		return nonCompoundEvents.filter(
			(event) =>
				event._id === currentStepEventId || !selectedEventIds.has(event._id),
		);
	};

	return (
		<Grid item xs={12}>
			<Box
				sx={{
					border: 1,
					borderColor: errors?.compoundSteps ? "error.main" : "divider",
					borderRadius: 1,
					p: 2,
					mt: 1,
				}}
			>
				<Typography variant="subtitle1" sx={{ mb: 2 }}>
					Compound Event Configuration
				</Typography>

				{/* Steps */}
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Steps
				</Typography>

				{compoundConfig.steps.map((step, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							mb: 1.5,
							p: 1.5,
							border: 1,
							borderColor: "divider",
							borderRadius: 1,
							backgroundColor: "background.paper",
						}}
					>
						{/* Order number */}
						<Typography
							variant="body2"
							sx={{
								fontWeight: "bold",
								minWidth: 28,
								textAlign: "center",
							}}
						>
							{step.order}
						</Typography>

						{/* Event dropdown */}
						<Autocomplete
							sx={{ flex: 1 }}
							size="small"
							options={getAvailableEventsForStep(index)}
							loading={eventsLoading}
							value={
								nonCompoundEvents.find((e) => e._id === step.eventId) || null
							}
							onChange={(_, newValue) =>
								updateStep(index, { eventId: newValue?._id || "" })
							}
							getOptionLabel={(option) => option.name}
							groupBy={(option) => option.validationType}
							isOptionEqualToValue={(option, value) => option._id === value._id}
							renderOption={(props, option) => (
								<li {...props} key={option._id}>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1,
											width: "100%",
										}}
									>
										<span>{option.name}</span>
										{option.isSubEvent && (
											<Chip
												label="Compound-Only"
												size="small"
												color="info"
												variant="outlined"
												sx={{ ml: "auto" }}
											/>
										)}
									</Box>
								</li>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									label={`Step ${step.order} Event`}
									placeholder="Search events..."
									error={!!errors?.compoundSteps && !step.eventId}
								/>
							)}
						/>

						{/* Required checkbox */}
						<FormControlLabel
							control={
								<Checkbox
									checked={step.required !== false}
									onChange={(e) =>
										updateStep(index, { required: e.target.checked })
									}
									size="small"
								/>
							}
							label="Required"
							sx={{ mx: 0.5, whiteSpace: "nowrap" }}
						/>

						{/* Reorder buttons */}
						<IconButton
							size="small"
							onClick={() => moveStep(index, "up")}
							disabled={index === 0}
						>
							<ArrowUpwardIcon fontSize="small" />
						</IconButton>
						<IconButton
							size="small"
							onClick={() => moveStep(index, "down")}
							disabled={index === compoundConfig.steps.length - 1}
						>
							<ArrowDownwardIcon fontSize="small" />
						</IconButton>

						{/* Delete button */}
						<IconButton
							size="small"
							color="error"
							onClick={() => removeStep(index)}
							disabled={compoundConfig.steps.length <= 2}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Box>
				))}

				{errors?.compoundSteps && (
					<Typography variant="caption" color="error" sx={{ ml: 1 }}>
						{errors.compoundSteps}
					</Typography>
				)}

				{/* Add step button */}
				<Button
					size="small"
					startIcon={<AddIcon />}
					onClick={addStep}
					disabled={compoundConfig.steps.length >= MAX_STEPS}
					sx={{ mt: 1, mb: 2 }}
				>
					Add Step ({compoundConfig.steps.length}/{MAX_STEPS})
				</Button>

				{/* Config fields */}
				<Grid container spacing={2} sx={{ mt: 1 }}>
					<Grid item xs={12} sm={6}>
						<FormControlLabel
							control={
								<Switch
									checked={compoundConfig.allowPartialSubmission || false}
									onChange={(e) =>
										onChange({
											...compoundConfig,
											allowPartialSubmission: e.target.checked,
										})
									}
								/>
							}
							label="Allow Partial Submission"
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth size="small">
							<InputLabel>Completion Requirement</InputLabel>
							<Select
								value={
									compoundConfig.completionRequirement ||
									CompletionRequirement.ALL_REQUIRED
								}
								label="Completion Requirement"
								onChange={(e) =>
									onChange({
										...compoundConfig,
										completionRequirement: e.target
											.value as CompletionRequirement,
									})
								}
							>
								{Object.values(CompletionRequirement).map((req) => (
									<MenuItem key={req} value={req}>
										{COMPLETION_LABELS[req]}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				</Grid>
			</Box>
		</Grid>
	);
};
